import { StatisticsReport } from '@api/models/types';
import useAnalysisSelectedFuelType from '@common/hooks/useAnalysisSelectedFuelType';
import { getFeatureToggles } from '@common/permissions/selectors';
import { CalculationParams } from '@features/settings/reducer';
import { DriverFuelType } from '@pages/driverAnalysis/driverAnalysisReducer';
import { getColumn, hydrateColumns } from '@utils/columns';
import { getSummaryDieselAndElectricValues, getSummaryValue } from '@utils/getSummaryValue';
import { shouldShowDieselValue, shouldShowElectricValue } from '@utils/ratingVisibility';
import {
    shouldHideAllSummaryGraphDataForElectricOnly,
    shouldHideElectricSummaryGraphData,
    shouldHideSummarySectionByFeatureFlag,
} from '@utils/summaryFuelTypeRules';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { fuelAndElectricAverageIdlingConsumption, fuelEfficiency, idlingConsumption } from '../../columns/consumption';
import { Column } from '../../columns/createColumn';
import { fuelAndElectricIdlingTime, idlingTime, idlingTimeBev } from '../../columns/time';
import { FUEL_ELECTRIC_EFFICIENCY } from '../../constants/units';
import Chevron from '../Chevron';
import Panel, { PanelContent, PanelHeader } from '../Panel';
import SkeletonLoader from '../SkeletonLoader';
import Content from './Content';
import {
    Data,
    DieselAndElectric,
    Errored,
    Formatter,
    Loaded,
    Loading,
    Section,
    Status,
    SummaryStatus,
    Values,
} from './types';

type ElectricStats =
    | (StatisticsReport & { values?: Values; recuperationValues?: Values })
    | { values?: Values; recuperationValues?: Values };

const createDefaultSection = (hasDieselAndElectricData: boolean) => (column: Column): Section => ({
    key: column.dataField,
    title: (
        <React.Fragment>
            {column.icon ? <span className={`${column.icon} text-size-20 margin-right-5`} /> : null}
            <FormattedMessage id={column.labelId} />
        </React.Fragment>
    ),
    unit: hasDieselAndElectricData
        ? {
              diesel: column.unit,
              electric: undefined,
          }
        : column.unit,
    stats: {},
    tabValue: (
        <SkeletonLoader height={'50%'} yOffset={5}>
            <span>------</span>
        </SkeletonLoader>
    ),
    formatter: hasDieselAndElectricData
        ? {
              diesel: column.formatter,
              electric: undefined,
          }
        : column.formatter,
    values: hasDieselAndElectricData
        ? {
              diesel: undefined,
              electric: {
                  values: undefined,
                  recuperationValues: undefined,
              },
          }
        : [],
});

const decorateWithValues = ({ dieselData }: { dieselData?: Data[] }) => (section: Section): Section => {
    const stats = dieselData?.find(d => d.attribute === section.key) || { values: [] };

    return {
        ...section,
        stats,
        tabValue: getSummaryValue(section, stats),
        values: {
            diesel: stats.values,
        },
    };
};

const MAPPING_BETWEEN_DIESEL_AND_ELECTRIC_COLUMNS: Record<string, string> = {
    drivingConsumption: 'electricAverageDrivingConsumption',
    idlingConsumption: 'electricAverageIdlingConsumption',
    fuelConsumption: 'electricOperatingConsumption',
    operatingFuelConsumption: 'electricAverageOperatingConsumption',
    distanceFuelConsumption: 'electricAverageOperatingConsumptionRange',
    fuelEfficiency: 'electricEfficiency',
    foresightedDrivingPercentageV2: 'electricForesightedDrivingPercentage',
    overspeedRating: 'electricOverspeedRating',
    cruiseControlRatingV2: 'electricCruiseControlRating',
    accelerationPedalRatingV2: 'electricAccelerationPedalRating',
};

const RECUPERATION_MAPPING: Record<string, string> = {
    drivingConsumption: 'electricRecuperationPerKm',
    idlingConsumption: 'electricRecuperationPerHour',
    fuelConsumption: 'electricRecuperation',
    operatingFuelConsumption: 'electricRecuperationPerKm',
    distanceFuelConsumption: 'electricRecuperationKmPerKwh',
};

const GENERAL_KPIS = [
    'coastingRating',
    'accelerationPedalRatingV2',
    'brakingRating',
    'cruiseControlRatingV2',
    'overspeedRating',
];

const decorateWithDieselAndElectricValues = (
    {
        dieselData,
        electricData,
    }: {
        dieselData?: Data[];
        electricData?: Data[];
    },
    shouldShowBothValues: boolean,
    isDriver: boolean,
    selectedFuelType: DriverFuelType,
    calculationParams?: CalculationParams,
    shouldConsiderElectricParams: boolean = true,
    attributes?: { key: string; unit: string | null }[]
) => (section: Section): Section => {
    const mappedDieselAndElectricSections = Object.keys(MAPPING_BETWEEN_DIESEL_AND_ELECTRIC_COLUMNS);
    const existsMappingBetweenDieselAndElectricSection = mappedDieselAndElectricSections.includes(section.key);

    let dieselStats = dieselData?.find(d => d.attribute === section.key) || {
        values: undefined,
    };

    let electricStats: ElectricStats = electricData?.find(d => {
        return existsMappingBetweenDieselAndElectricSection
            ? d.attribute === MAPPING_BETWEEN_DIESEL_AND_ELECTRIC_COLUMNS[section.key]
            : d.attribute === section.key;
    }) || { values: undefined };

    if (section.key.toLowerCase().includes('consumption')) {
        const recuperationValues =
            electricData?.find(d => d.attribute === RECUPERATION_MAPPING[section.key])?.values || undefined;
        electricStats = { ...electricStats, recuperationValues };
    }

    if (calculationParams && shouldConsiderElectricParams) {
        const showDiesel = shouldShowDieselValue(section.key, calculationParams);
        const showElectric = shouldShowElectricValue(section.key, calculationParams, shouldConsiderElectricParams);

        if (!showDiesel && dieselStats.values !== undefined) {
            dieselStats = { values: undefined };
        }

        if (!showElectric && electricStats.values !== undefined) {
            electricStats = { values: undefined, recuperationValues: undefined };
        }
    }

    const shouldHideAllForElectricOnly = shouldHideAllSummaryGraphDataForElectricOnly(section.key, selectedFuelType);
    const shouldHideElectricOnly = shouldHideElectricSummaryGraphData(section.key, selectedFuelType);

    if (shouldHideAllForElectricOnly) {
        dieselStats = { values: undefined };
        electricStats = { values: undefined, recuperationValues: undefined };
    } else if (shouldHideElectricOnly) {
        electricStats = { values: undefined, recuperationValues: undefined };
    }

    const dieselUnit = (section.unit as DieselAndElectric<string | undefined>).diesel;
    const dieselFormatter = (section.formatter as DieselAndElectric<Formatter>).diesel;

    let electricUnit, electricFormatter;
    if (existsMappingBetweenDieselAndElectricSection) {
        const electricColumnKey = MAPPING_BETWEEN_DIESEL_AND_ELECTRIC_COLUMNS[section.key];
        const rawElectricColumn = getColumn(electricColumnKey);
        const hydratedElectricColumn = hydrateColumns([rawElectricColumn as Column], attributes || [], {
            plain: true,
        })[0];

        electricUnit = hydratedElectricColumn?.unit;
        electricFormatter = hydratedElectricColumn?.formatter;
    } else {
        electricUnit = dieselUnit;
        electricFormatter = dieselFormatter;
    }

    let newTitle = section.title;

    const hasDieselData = dieselData?.find(data => data.attribute === section.key);
    const hasElectricData = electricData?.length !== undefined;

    if (hasElectricData) {
        const labelIdMapping = hasDieselData
            ? {
                  [fuelEfficiency.key]: FUEL_ELECTRIC_EFFICIENCY,
                  [idlingConsumption.key]: fuelAndElectricAverageIdlingConsumption.labelId,
                  [idlingTime.key]: fuelAndElectricIdlingTime.labelId,
              }
            : {
                  [fuelEfficiency.key]: getColumn(MAPPING_BETWEEN_DIESEL_AND_ELECTRIC_COLUMNS[section.key])?.labelId,
                  [idlingConsumption.key]: getColumn(MAPPING_BETWEEN_DIESEL_AND_ELECTRIC_COLUMNS[section.key])?.labelId,
                  [idlingTime.key]: idlingTimeBev.labelId,
              };

        if (labelIdMapping[section.key]) {
            newTitle = (
                <React.Fragment>
                    <FormattedMessage id={labelIdMapping[section.key]} />
                </React.Fragment>
            );
        }
    }

    const isGeneralKPI = GENERAL_KPIS.includes(section.key);
    let showBothValues = shouldShowBothValues && (!isDriver || !isGeneralKPI);
    // hide both values until truESOP is on for total rating, driving distance and gross weight
    if (electricData && dieselData) {
        showBothValues = true;
    }

    if (selectedFuelType !== 0) {
        showBothValues = false;
    }

    return {
        ...section,
        title: newTitle,
        unit: {
            diesel: dieselUnit,
            electric: electricUnit,
        },
        stats: { diesel: dieselStats, electric: electricStats },
        tabValue: getSummaryDieselAndElectricValues({
            diesel: {
                stats: dieselStats,
                unit: dieselUnit,
                formatter: dieselFormatter,
            },
            electric: { stats: electricStats, unit: electricUnit, formatter: electricFormatter },
            shouldShowBothValues: showBothValues,
            selectedFuelType,
        }),
        formatter: { diesel: dieselFormatter, electric: electricFormatter },
        values: {
            diesel: dieselStats.values,
            electric: {
                values: electricStats.values,
                recuperationValues: electricStats.recuperationValues,
            },
        },
    };
};

export const createLoading = (): Loading => ({ type: SummaryStatus.LOADING });
export const createLoadedWithDieselAndElectricData = ({
    dieselData,
    electricData,
}: {
    dieselData?: Data[];
    electricData?: Data[];
}): Loaded => ({
    type: SummaryStatus.LOADED,
    data: { dieselData, electricData },
});
export const createLoaded = ({ dieselData, electricData }: { dieselData?: Data[]; electricData?: Data[] }): Loaded => ({
    type: SummaryStatus.LOADED,
    data: { dieselData, electricData },
});
export const createError = (data?: React.ReactNode): Errored => ({ type: SummaryStatus.ERROR, data });

type Props = {
    header?: React.ReactNode;
    columns: Column[];
    entities: unknown[];
    children?: ({ activeSection }: { activeSection: Section }) => React.ReactNode;
    status: Status;
    isExpanded?: boolean;
    onHeaderClick?: () => void;
    activeTab: string | null;
    onTabChange: (s: string | null) => void;
    account?: string;
    location: string;
    hasDieselAndElectricData?: boolean;
    shouldShowBothValues?: boolean;
    highlightColor?: string;
    calculationParams?: CalculationParams;
    shouldConsiderElectricParams?: boolean;
    attributes?: { key: string; unit: string | null }[];
};

export const LoadedContent: React.FC<{
    entities: unknown[];
    children: ({ activeSection }: { activeSection: Section }) => React.ReactNode;
    isExpanded: boolean;
    onHeaderClick?: () => void;
    data: Data[] | { dieselData?: Data[]; electricData?: Data[] };
    activeTab: string | null;
    onTabChange: (s: string | null) => void;
    rawSections: Section[];
    account?: string;
    location: string;
    hasDieselAndElectricData?: boolean;
    shouldShowBothValues: boolean;
    highlightColor?: string;
    calculationParams?: CalculationParams;
    shouldConsiderElectricParams?: boolean;
    attributes?: { key: string; unit: string | null }[];
    selectedFuelType: DriverFuelType;
}> = ({
    entities,
    children,
    rawSections,
    isExpanded,
    data,
    onTabChange,
    activeTab,
    account,
    location,
    hasDieselAndElectricData,
    shouldShowBothValues,
    highlightColor,
    calculationParams,
    shouldConsiderElectricParams = true,
    attributes,
    selectedFuelType,
}) => {
    const isDriver = location === 'drivingAnalysis';

    const sections = useMemo(
        () =>
            hasDieselAndElectricData
                ? rawSections.map(
                      decorateWithDieselAndElectricValues(
                          data as { dieselData: Data[]; electricData: Data[] },
                          shouldShowBothValues,
                          isDriver,
                          selectedFuelType,
                          calculationParams,
                          shouldConsiderElectricParams,
                          attributes
                      )
                  )
                : rawSections.map(decorateWithValues(data as { dieselData: Data[] })),
        [
            hasDieselAndElectricData,
            rawSections,
            data,
            shouldShowBothValues,
            calculationParams,
            shouldConsiderElectricParams,
            attributes,
            isDriver,
            selectedFuelType,
        ]
    );

    const activeSection = sections.find(s => s.key === activeTab) || sections[0];

    return (
        <Content
            account={account}
            location={location}
            activeTab={activeTab}
            entities={entities}
            sections={sections}
            isExpanded={isExpanded}
            setActiveTab={onTabChange}
            highlightColor={highlightColor}
        >
            {activeSection ? children({ activeSection }) : null}
        </Content>
    );
};
const Summary = ({
    header = null,
    columns = [],
    entities = [],
    children = () => null,
    status = createLoading(),
    isExpanded = true,
    onHeaderClick,
    activeTab,
    onTabChange,
    account,
    location,
    hasDieselAndElectricData = false,
    shouldShowBothValues = false,
    highlightColor,
    calculationParams,
    shouldConsiderElectricParams = true,
    attributes,
}: Props) => {
    const featureToggles = useSelector(getFeatureToggles);
    const hideStandSAndDrivingConsumption = Boolean(featureToggles.hideStandSAndDrivingConsumption);
    const { selectedFuelType } = useAnalysisSelectedFuelType(location);
    const defaultSections = useMemo(
        () =>
            columns.map(createDefaultSection(hasDieselAndElectricData)).filter(section => {
                const shouldHide = shouldHideSummarySectionByFeatureFlag(
                    section.key,
                    selectedFuelType,
                    hideStandSAndDrivingConsumption
                );
                return !shouldHide;
            }),
        [columns, hasDieselAndElectricData, selectedFuelType, hideStandSAndDrivingConsumption]
    );
    if (!activeTab || (defaultSections.length && !_.find(defaultSections, ['key', activeTab]))) {
        activeTab = _.get(defaultSections, '[0].key', null);
    }
    let content = null;
    switch (status.type) {
        case SummaryStatus.LOADED: {
            content = (
                <LoadedContent
                    entities={entities}
                    onTabChange={onTabChange}
                    data={status.data}
                    rawSections={defaultSections}
                    activeTab={activeTab}
                    isExpanded={isExpanded}
                    account={account}
                    location={location}
                    hasDieselAndElectricData={hasDieselAndElectricData}
                    shouldShowBothValues={shouldShowBothValues}
                    highlightColor={highlightColor}
                    calculationParams={calculationParams}
                    shouldConsiderElectricParams={shouldConsiderElectricParams}
                    attributes={attributes}
                    selectedFuelType={selectedFuelType}
                >
                    {children}
                </LoadedContent>
            );
            break;
        }
        case SummaryStatus.LOADING: {
            const activeSection = defaultSections.find(s => s.key === activeTab);
            content = (
                <Content
                    isLoading={true}
                    activeTab={activeTab}
                    entities={entities}
                    sections={defaultSections}
                    isExpanded={isExpanded}
                    setActiveTab={onTabChange}
                    account={account}
                    location={location}
                >
                    {activeSection ? children({ activeSection }) : null}
                </Content>
            );
            break;
        }
        case SummaryStatus.ERROR:
            content = status.data as React.ReactNode;
            break;
    }

    return (
        <Panel>
            <PanelHeader>
                <div
                    onClick={onHeaderClick}
                    className="display-flex justify-content-center align-items-center cursor-pointer"
                    data-test="summary-header"
                >
                    <div className="flex-1-1">{header}</div>
                    <Chevron isOpen={isExpanded} />
                </div>
            </PanelHeader>
            <PanelContent>{content}</PanelContent>
        </Panel>
    );
};

export default Summary;
